import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { EnrichmentState } from "../api/types";
import { shortDate } from "../lib/format";
import { Spinner } from "./Spinner";
import { Badge, type Tone } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Fact, FactActions, FactSaid } from "../ui/Facts";

/* The five states, as the tones the shared Badge knows. A tone is named for
   what it means, so Found and a done job look alike on every screen. */
const TONE: Record<string, Tone> = {
  never_queued: "quiet",
  queued: "waiting",
  found: "ok",
  no_result: "quiet",
  unreachable: "refusal",
};

/* What the web enrichment worker knows about a record.
 *
 * The five states are five different facts and the words keep them apart. A
 * search that answered and found nothing is not a search that never happened,
 * and it is not a provider that was down. Collapsing those on 8 September
 * closed 747 records as answers when a search provider had run out of credit.
 *
 * docs/writing-standards.md governs these sentences, which is why the view
 * returns a machine value and the words live here.
 *
 * The label is what a person reads. The detail is a tooltip and is not printed
 * on the screen. Printing both meant a status and an explanation of the status
 * side by side on every record, which is five sentences of reading to learn one
 * word. */
const WORDS: Record<string, { label: string; detail: string }> = {
  never_queued: { label: "Not searched", detail: "Not searched yet." },
  queued: { label: "Waiting", detail: "In the queue. This can take hours." },
  found: { label: "Found", detail: "Pages were found and read." },
  no_result: { label: "Nothing", detail: "Searched. No useful result." },
  unreachable: { label: "Failed", detail: "The search could not run." },
};

/* On a record, where there is room for the sentence and a reader is asking
   about this one row. */
const FULL: Record<string, string> = {
  never_queued: "Not searched",
  no_result: "Nothing found",
  unreachable: "Search failed",
};

/* One word, not a sentence, because this is a table column.
 *
 * `compact` is the list. Most of the book has never been searched, so that
 * column was a wall of "Not searched" a reader had to get past before
 * noticing the rows where something actually happened. A dash puts it with
 * every other value the engine holds that tells the reader nothing, which is
 * the convention format.ts already sets, and leaves the eye free for the four
 * states that are news.
 *
 * The five states stay five states. Collapsing them on 8 September closed 747
 * records as answers when the search provider had run out of credit. */
export function EnrichmentBadge(
  { state, compact = false }: { state?: string | null | undefined; compact?: boolean | undefined },
) {
  if (!state) return null;
  const w = WORDS[state];
  if (compact && state === "never_queued" && w) {
    return <span className="muted" title={w.detail}>-</span>;
  }
  return (
    <Badge tone={TONE[state] ?? "plain"} title={w?.detail ?? state}>
      {(compact ? w?.label : FULL[state] ?? w?.label) ?? state}
    </Badge>
  );
}

export function useEnrichment(token: string, entityType: string, entityId: string | null) {
  return useQuery({
    queryKey: ["enrichment", entityType, entityId],
    enabled: Boolean(entityId),
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("enrichment_state")
        .select("state,requested_at,requested_by,reason,last_looked_at,outcome,attempts,batch,requeueable_after")
        .eq("entity_type", entityType)
        .eq("entity_id", entityId as string)
        .limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as EnrichmentState | null;
    },
  });
}

/** Enrichment state for a page of rows, keyed by id, in one request. */
export function useEnrichmentForIds(token: string, entityType: string, ids: readonly string[]) {
  return useQuery({
    queryKey: ["enrichment-page", entityType, ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("enrichment_state")
        .select("entity_id,state,last_looked_at")
        .eq("entity_type", entityType)
        .in("entity_id", ids as string[])
        // One row per id at most, so this is the id list's own length rather
        // than a guess. Written down because "it cannot return more than the
        // ids I asked for" is a thing that stays true until somebody changes
        // the view, and an unbounded query is a defect whether or not today's
        // data would fill it.
        .limit(ids.length);
      if (error) throw new Error(apiErrorMessage(error));
      const by: Record<string, EnrichmentState> = {};
      for (const r of (data ?? []) as EnrichmentState[]) if (r.entity_id) by[r.entity_id] = r;
      return by;
    },
  });
}

/* The web lookup, as facts on the Details tab: the state, the facts that
   exist about the last request, and one link that sends the record. James,
   2026-10-23: no reason is asked for, and the engine records who asked
   (api.queue_enrichment, engine migration 281). The page header's Run web
   enrichment sends it the same way. */
export function LookupFacts({
  token,
  entityType,
  entityId,
  open: pressed,
  onOpenChange,
}: {
  token: string;
  entityType: string;
  entityId: string;
  /** True when the page header's Run web enrichment was pressed. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const q = useEnrichment(token, entityType, entityId);
  const queryClient = useQueryClient();
  const [said, setSaid] = useState<string | null>(null);
  const e = q.data;
  const state = e?.state ?? "never_queued";

  const queue = useMutation({
    mutationFn: () =>
      callFunction<boolean>(token, "queue_enrichment", {
        p_entity_type: entityType,
        p_entity_id: entityId,
      }),
    onSuccess: (accepted) => {
      // The engine returns false rather than raising when the record is
      // already waiting, so a quiet false has to be reported as a refusal.
      // Saying "queued" on a refusal is how somebody waits for work that is
      // not happening.
      setSaid(accepted
        ? "Queued. This can take hours."
        : "Not queued. It is already waiting for web enrichment.");
      queryClient.invalidateQueries({ queryKey: ["enrichment", entityType, entityId] });
      queryClient.invalidateQueries({ queryKey: ["enrichment-page", entityType] });
    },
    onError: (err: Error) => setSaid(err.message),
  });
  const send = () => { setSaid(null); queue.mutate(); };

  // The header's action sends it once, then hands the page its tool back.
  // Only a change of pressed runs this, so a render does not send it again.
  useEffect(() => {
    if (!pressed) return;
    onOpenChange?.(false);
    if (!queue.isPending) send();
  }, [pressed]);

  return (
    <>
      <Fact label="Web enrichment" id="lookup">
        {q.isLoading ? <Spinner />
          : q.isError ? <span className="refusal">{(q.error as Error).message}</span>
          : (
            <>
              <EnrichmentBadge state={state} />
              {e?.last_looked_at ? <span className="muted"> · searched {shortDate(e.last_looked_at)}</span> : null}
            </>
          )}
      </Fact>
      {e?.requested_at ? (
        <Fact label="Asked for">
          {shortDate(e.requested_at)}
          {e.requested_by ? <span className="muted"> by {e.requested_by}</span> : null}
        </Fact>
      ) : null}
      {e?.reason ? <Fact label="Reason">{e.reason}</Fact> : null}
      {e?.attempts ? <Fact label="Attempts">{e.attempts}</Fact> : null}
      {/* Shown because it is why the link would be refused. Better to say the
          date than to let somebody press a button that cannot work. */}
      {e?.requeueable_after ? <Fact label="Can search again after">{shortDate(e.requeueable_after)}</Fact> : null}
      <FactActions>
        <Button kind="link" size="compact-sm" disabled={q.isLoading || queue.isPending} onClick={send}>
          {queue.isPending ? <><Spinner />Sending</> : "Run web enrichment"}
        </Button>
      </FactActions>
      {said ? <FactSaid error={!said.startsWith("Queued")}>{said}</FactSaid> : null}
    </>
  );
}
